import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, Legend } from "recharts";
import { Flame, TrendingUp, Users, CalendarClock, PhoneCall, Megaphone } from "lucide-react";

const COLORS = ["#1A9E5F", "#0F766E", "#38BDF8", "#F59E0B", "#64748B", "#334155", "#8B5CF6"];

const Kpi = ({ label, value, icon: Icon }) => (
  <Card className="p-5 shadow-sm">
    <div className="flex items-start justify-between">
      <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p><p className="mt-2 font-mono text-3xl font-bold text-slate-900">{value}</p></div>
      <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><Icon className="h-5 w-5" /></div>
    </div>
  </Card>
);

const Donut = ({ title, data }) => (
  <Card className="shadow-sm"><CardHeader className="pb-2"><CardTitle className="font-heading text-base">{title}</CardTitle></CardHeader>
    <CardContent><ResponsiveContainer width="100%" height={240}>
      <PieChart><Pie data={data} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2} stroke="none">
        {data.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie>
        <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} /><Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} /></PieChart>
    </ResponsiveContainer></CardContent></Card>
);

const Bars = ({ title, data }) => (
  <Card className="shadow-sm"><CardHeader className="pb-2"><CardTitle className="font-heading text-base">{title}</CardTitle></CardHeader>
    <CardContent><ResponsiveContainer width="100%" height={240}>
      <BarChart data={data} layout="vertical" margin={{ left: 20 }}><XAxis type="number" hide /><YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
        <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} /><Bar dataKey="value" fill="#1A9E5F" radius={[0, 4, 4, 0]} /></BarChart>
    </ResponsiveContainer></CardContent></Card>
);

export default function Analytics() {
  const [d, setD] = useState(null);
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/analytics").then(({ data }) => setD(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/marketing/attribution").then(({ data }) => setM(data)).catch(() => {}); }, []);
  if (!d) return <div className="space-y-6"><Skeleton className="h-8 w-48" /><div className="grid gap-6 md:grid-cols-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-72" />)}</div></div>;

  const li = d.lead_intelligence, ci = d.closure_intelligence, ai = d.appointment_intelligence;
  const vi = d.call_intelligence;
  const temp = [{ name: "Hot", value: li.temperature.hot }, { name: "Warm", value: li.temperature.warm }, { name: "Cold", value: li.temperature.cold }];

  return (
    <div className="animate-fade-up space-y-6" data-testid="analytics-page">
      <div><h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">Analytics</h1>
        <p className="mt-1 text-sm text-slate-500">Lead, closure & appointment intelligence</p></div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
        <Kpi label="Total Leads" value={li.total} icon={Users} />
        <Kpi label="Conversion Rate" value={`${ci.conversion_rate}%`} icon={TrendingUp} />
        <Kpi label="Appointment Rate" value={`${ci.appointment_rate}%`} icon={CalendarClock} />
        <Kpi label="Hot Leads" value={li.temperature.hot} icon={Flame} />
      </div>

      <h2 className="font-heading text-lg font-semibold text-slate-800">Lead Intelligence</h2>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Donut title="Temperature (HOT/WARM/COLD)" data={temp} />
        <Donut title="By Source" data={li.by_source} />
        <Bars title="By Status" data={li.by_status} />
      </div>

      <h2 className="font-heading text-lg font-semibold text-slate-800">Closure Intelligence</h2>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="shadow-sm"><CardHeader className="pb-2"><CardTitle className="font-heading text-base">Follow-up Performance</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="flex justify-between"><span className="text-slate-500">Pending</span><span className="font-mono">{ci.follow_ups.pending}</span></p>
            <p className="flex justify-between"><span className="text-slate-500">Overdue</span><span className="font-mono text-red-600">{ci.follow_ups.overdue}</span></p>
            <p className="flex justify-between"><span className="text-slate-500">Completed</span><span className="font-mono">{ci.follow_ups.completed}</span></p>
            <p className="flex justify-between"><span className="text-slate-500">Per converted lead</span><span className="font-mono">{ci.follow_ups.per_converted}</span></p>
          </CardContent></Card>
        {ci.win_loss.length > 0 ? <Bars title="Win / Loss Reasons" data={ci.win_loss} /> : <Card className="shadow-sm"><CardHeader className="pb-2"><CardTitle className="font-heading text-base">Win / Loss Reasons</CardTitle></CardHeader><CardContent className="py-10 text-center text-sm text-slate-400">No lost leads recorded.</CardContent></Card>}
        <Card className="shadow-sm"><CardHeader className="pb-2"><CardTitle className="font-heading text-base">Executive Performance</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {ci.executives.length === 0 ? <p className="text-slate-400">No owners assigned.</p> : ci.executives.map((e, i) => (
              <p key={i} className="flex justify-between"><span className="text-slate-700">{e.owner}</span><span className="font-mono text-slate-500">{e.converted}/{e.leads} converted</span></p>
            ))}
          </CardContent></Card>
      </div>

      <h2 className="font-heading text-lg font-semibold text-slate-800">Appointment Intelligence</h2>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Donut title="By Status" data={ai.by_status} />
        <Bars title="By Doctor" data={ai.by_doctor} />
        <Bars title="By Service" data={ai.by_service} />
      </div>

      <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-slate-800"><Megaphone className="h-5 w-5 text-emerald-600" /> Marketing Intelligence</h2>
      {m && m.by_source.length > 0 ? (
        <Card className="shadow-sm" data-testid="marketing-attribution">
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="font-heading text-base">Attribution by Source</CardTitle>
            {!m.has_spend_data && <Badge className="bg-slate-100 text-slate-500">Add ad spend in Settings for CPL / ROI</Badge>}
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="py-2 pr-4">Source</th><th className="py-2 pr-4">Leads</th><th className="py-2 pr-4">Appts</th>
                <th className="py-2 pr-4">Patients</th><th className="py-2 pr-4">Lead→Appt</th><th className="py-2 pr-4">Lead→Patient</th>
                <th className="py-2 pr-4">Revenue</th><th className="py-2 pr-4">Spend</th><th className="py-2 pr-4">CPL</th><th className="py-2 pr-4">ROI</th>
              </tr></thead>
              <tbody>
                {m.by_source.map((r, i) => (
                  <tr key={i} className="border-b border-slate-50" data-testid="marketing-source-row">
                    <td className="py-2 pr-4 font-medium text-slate-800">{r.source}</td>
                    <td className="py-2 pr-4 font-mono">{r.leads}</td>
                    <td className="py-2 pr-4 font-mono">{r.appointments}</td>
                    <td className="py-2 pr-4 font-mono">{r.patients}</td>
                    <td className="py-2 pr-4 font-mono text-slate-500">{r.lead_to_appointment}%</td>
                    <td className="py-2 pr-4 font-mono text-slate-500">{r.lead_to_patient}%</td>
                    <td className="py-2 pr-4 font-mono">₹{(r.revenue || 0).toLocaleString()}</td>
                    <td className="py-2 pr-4 font-mono">{r.spend != null ? `₹${r.spend.toLocaleString()}` : "—"}</td>
                    <td className="py-2 pr-4 font-mono">{r.cost_per_lead != null ? `₹${r.cost_per_lead}` : "—"}</td>
                    <td className={`py-2 pr-4 font-mono ${r.roi != null && r.roi >= 0 ? "text-emerald-600" : r.roi != null ? "text-red-600" : "text-slate-400"}`}>{r.roi != null ? `${r.roi}%` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-sm"><CardContent className="py-10 text-center text-sm text-slate-400" data-testid="marketing-empty">No lead source data yet.</CardContent></Card>
      )}

      <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-slate-800"><PhoneCall className="h-5 w-5 text-emerald-600" /> Call Intelligence</h2>
      {vi && vi.has_data ? (
        <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
            <Kpi label="Total Calls" value={vi.total_calls} icon={PhoneCall} />
            <Kpi label="Calls / Lead" value={vi.calls_per_lead} icon={Users} />
            <Kpi label="Calls Before Appt" value={vi.avg_calls_before_appointment} icon={CalendarClock} />
            <Kpi label="Call → Appt Rate" value={`${vi.call_to_appointment_rate}%`} icon={TrendingUp} />
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Bars title="Call Outcomes" data={vi.by_outcome} />
            {vi.by_intent.length > 0 ? <Bars title="Call Intents" data={vi.by_intent} />
              : <Card className="shadow-sm"><CardHeader className="pb-2"><CardTitle className="font-heading text-base">Call Intents</CardTitle></CardHeader><CardContent className="py-10 text-center text-sm text-slate-400">No intents captured yet.</CardContent></Card>}
          </div>
        </>
      ) : (
        <Card className="shadow-sm"><CardContent className="py-10 text-center text-sm text-slate-400" data-testid="call-intel-empty">No voice calls recorded yet. Start a test call from the Calls page to populate call intelligence.</CardContent></Card>
      )}
    </div>
  );
}
